import dayjs from 'dayjs'
import { ReactElement } from 'react'
import {
  Box,
  Card,
  Chip,
  LinearProgress,
  Skeleton,
  Typography,
} from '@mui/material'
import {
  CalendarIcon,
  CorrectIcon,
  CourseIcon,
  IncorrectIcon,
  UserIcon,
} from 'src/v3/core/icons'
import { AssignmentDetailV3 } from 'src/v3/manage/manage.slice'
import {
  getProgressColor,
  getIsOverdue,
  getCompletionRate,
} from '../utils/helpers'

type AssignmentDetailsCardProps = {
  assignment: AssignmentDetailV3 | null
  isLoading: boolean
}

function AssignmentDetailsCardSkeleton(): ReactElement {
  return (
    <Card className="p-6 mb-6">
      <Box className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <Box>
          <Skeleton variant="text" width={180} height={28} className="mb-4" />
          <Box className="flex flex-col gap-4">
            <Box className="flex items-center gap-2">
              <Skeleton variant="text" width={70} height={20} />
              <Skeleton variant="rounded" width={100} height={24} />
            </Box>
            <Box className="flex items-center gap-2">
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width={60} height={20} />
              <Skeleton variant="text" width={100} height={20} />
            </Box>
            <Box className="flex items-center gap-2">
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width={70} height={20} />
              <Skeleton variant="text" width={100} height={20} />
            </Box>
            <Box className="flex items-center gap-2">
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width={60} height={20} />
              <Skeleton variant="text" width={80} height={20} />
            </Box>
            <Box className="flex items-center gap-2">
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width={50} height={20} />
              <Skeleton variant="text" width={90} height={20} />
            </Box>
          </Box>
        </Box>

        <Box>
          <Skeleton variant="text" width={140} height={28} className="mb-2" />
          <Box className="text-center md:pr-8 md:pt-10">
            <Skeleton
              variant="text"
              width={80}
              height={60}
              className="mx-auto mb-1"
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={12}
              sx={{ borderRadius: 6 }}
              className="mb-2"
            />
            <Skeleton
              variant="text"
              width={120}
              height={20}
              className="mx-auto"
            />
          </Box>
        </Box>

        <Box>
          <Skeleton variant="text" width={130} height={28} className="mb-2" />
          <Box className="grid grid-cols-1 gap-4">
            <Box
              className="p-2 text-center rounded-lg border-1"
              sx={{ borderColor: 'divider' }}
            >
              <Skeleton
                variant="text"
                width={50}
                height={48}
                className="mx-auto"
              />
              <Skeleton
                variant="text"
                width={80}
                height={20}
                className="mx-auto"
              />
              <Skeleton
                variant="text"
                width={70}
                height={16}
                className="mx-auto"
              />
            </Box>
            <Box
              className="p-2 text-center rounded-lg border-1"
              sx={{ borderColor: 'divider' }}
            >
              <Skeleton
                variant="text"
                width={50}
                height={48}
                className="mx-auto"
              />
              <Skeleton
                variant="text"
                width={80}
                height={20}
                className="mx-auto"
              />
              <Skeleton
                variant="text"
                width={70}
                height={16}
                className="mx-auto"
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Card>
  )
}

export default function AssignmentDetailsCard({
  assignment,
  isLoading = false,
}: AssignmentDetailsCardProps): ReactElement {
  if (!assignment || isLoading) {
    return <AssignmentDetailsCardSkeleton />
  }

  let isOverdue = getIsOverdue(assignment.dueDate || '')
  let completionRate = getCompletionRate(assignment.statistics)
  let isCompleted = completionRate === 100

  let statusColor = getProgressColor(completionRate, isOverdue)
  let statusText = isCompleted ? 'Completed' : isOverdue ? 'Overdue' : 'Active'
  let statusIcon = isCompleted ? (
    <CorrectIcon />
  ) : isOverdue ? (
    <IncorrectIcon />
  ) : null

  return (
    <Card className="p-6 mb-6">
      <Box className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <Box>
          <Typography variant="h6" component="h2" className="mb-4">
            Assignment Information
          </Typography>
          <Box className="flex flex-col gap-4">
            <Box className="flex items-center gap-2">
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ minWidth: 100 }}
              >
                Status:
              </Typography>
              <Chip
                size="small"
                color={statusColor}
                label={statusText}
                icon={statusIcon as ReactElement}
              />
            </Box>
            <Box className="flex items-center gap-2">
              <CalendarIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ minWidth: 80 }}
              >
                Created:
              </Typography>
              <Typography variant="body2">
                {dayjs(assignment.createdTime).format('MMM D, YYYY')}
              </Typography>
            </Box>
            {assignment.dueDate && (
              <Box className="flex items-center gap-2">
                <CalendarIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ minWidth: 80 }}
                >
                  Due Date:
                </Typography>
                <Typography variant="body2">
                  {dayjs(assignment.dueDate).format('MMM D, YYYY')}
                </Typography>
              </Box>
            )}
            <Box className="flex items-center gap-2">
              <CourseIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ minWidth: 80 }}
              >
                Courses:
              </Typography>
              <Typography variant="body2">
                {assignment.courses.length} required
              </Typography>
            </Box>
            <Box className="flex items-center gap-2">
              <UserIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ minWidth: 80 }}
              >
                Users:
              </Typography>
              <Typography variant="body2">
                {assignment.users.length} assigned
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box>
          <Typography variant="h6" component="h2" className="mb-2">
            Completion Progress
          </Typography>
          <Box className="text-center md:pr-8 md:pt-10">
            <Box sx={{ minWidth: 200 }}>
              <Typography
                variant="h3"
                color={statusColor}
                className="mb-1 font-extrabold"
              >
                {Math.round(completionRate)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={completionRate}
                sx={{ height: 12, borderRadius: 6 }}
                className="mb-2"
                color={statusColor}
              />
              <Typography variant="body2" color="text.secondary">
                {isCompleted
                  ? 'Assignment Complete'
                  : isOverdue
                    ? 'Past Due Date'
                    : 'In Progress'}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box>
          <Typography variant="h6" component="h2" className="mb-2">
            User Breakdown
          </Typography>
          <Box className="grid grid-cols-1 gap-4">
            <Box
              className="p-2 text-center rounded-lg border-1"
              sx={{
                borderColor: 'success.main',
              }}
            >
              <Typography
                variant="h4"
                color="success.main"
                className="font-extrabold"
              >
                {assignment.statistics?.completedUsers || 0}
              </Typography>
              <Typography
                variant="body2"
                color="success.main"
                className="font-bold"
              >
                Completed
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {completionRate}% of users
              </Typography>
            </Box>
            <Box
              className="p-2 text-center rounded-lg border-1"
              sx={{
                borderColor: 'primary.main',
              }}
            >
              <Typography
                variant="h4"
                color="primary.main"
                className="font-extrabold"
              >
                {assignment.statistics.inProgressUsers || 0}
              </Typography>
              <Typography
                variant="body2"
                color="primary.main"
                className="font-bold"
              >
                In Progress
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {100 - completionRate}% of users
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Card>
  )
}
